import React, { useState, useContext, useEffect } from "react";
import { Magic } from "magic-sdk";
import { navigate, Link } from "gatsby";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import Loading from "../../components/Loading";
import client from "../../apollo/client";
import { AuthUserContext } from "../../context/AuthUser";
import { apiBase } from "../../services/appenv";
import { getCurrentUser } from "../../services/api";
import { LOGIN } from "../../mutations";

let magic = null;

const LoginPassword = () => {
  const { setAuthUser } = useContext(AuthUserContext);

  const [email, setEmail] = useState("");
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [messageError, setMessageError] = useState({
    username: "",
    password: "",
  });

  const [loginMethod, setLoginMethod] = useState("password");

  const getLocalStorage = (key) => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(key);
    }
    return null;
  };

  const removeLocalStorage = (key) => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(key);
    }
  };

  const clearUserSession = () => {
    removeLocalStorage("token");
    setAuthUser({ logged: false, user: {} });
    client.clearStore();
  };

  const formValidate = (errors, ...data) => {
    const hasErrors = Object.values(errors).some((val) => val.length > 0);
    const hasEmptyFields = data.some((val) => val === "");
    return !hasErrors && !hasEmptyFields;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessageError((prevErrors) => ({
      ...prevErrors,
      [name]: value === "" ? "This field is required" : "",
    }));
    setCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    const token = getLocalStorage("token");

    if (token) {
      navigate("/home");
    } else {
      client.clearStore();
      removeLocalStorage("token");
      navigate("/login-idandpass");
    }

    if (typeof window !== "undefined") {
      magic = new Magic(process.env.GATSBY_MAGIC_PUBLISHABLE_KEY);
    }
  }, [navigate, client]);

  const [login, { loading: mutationLoading }] = useMutation(LOGIN, {
    onCompleted: async ({ login: { jwt, user } }) => {
      if (!jwt) return;
      localStorage.setItem("token", jwt);

      try {
        const response = await getCurrentUser(jwt);
        const { role, typecustomer, user_type } = response;

        if (response.blocked) {
          clearUserSession();
          toast.error("Account not yet activated.");
          return;
        }

        if (role?.id) {
          navigate(role.id === 4 ? "/orders" : "/home");
          setAuthUser({
            logged: true,
            typeCustomer: typecustomer ? typecustomer : "",
            role: role.id,
            type: user_type ? user_type?.name : "",
            user: { ...user, confirmed: typecustomer ? typecustomer : "" },
          });
          toast.success("Successful login");
        } else {
          clearUserSession();
          toast.error("The user does not have access");
        }
      } catch (error) {
        clearUserSession();
        toast.error("Failed to fetch user details");
      }
    },
    onError: () => {
      setAuthUser({ logged: false, user: {} });
      toast.error("Invalid username or password");
      navigate("/login");
    },
    refetchQueries: ["refreshCurrentUser"],
  });

  const handleLogin = (e) => {
    e.preventDefault();
    const { username, password } = credentials;

    if (formValidate(messageError, username, password)) {
      login({
        variables: {
          login: username,
          password,
        },
      });
    } else {
      toast.error("Please enter username and password");
    }
  };

  const handleMagicLinkLogin = async (e) => {
    e.preventDefault();

    if (typeof window === "undefined" || !magic) {
      toast.error("Cannot perform authentication in this environment");
      return;
    }

    setMagicLinkLoading(true);

    try {
      const didToken = await magic.auth.loginWithMagicLink({ email });
 

      const url = `${apiBase}/api/auth/magic/callback`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id_token: didToken }),
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.user && data.user.blocked) {
        clearUserSession();
        toast.error("Account not yet activated.");
        return;
      }

      localStorage.setItem("token", data.jwt);

      try {
        const userDetails = await getCurrentUser(data.jwt);
        const { role, typecustomer, user_type } = userDetails;

        if (userDetails.blocked) {
          clearUserSession();
          toast.error("Account not yet activated.");
          return;
        }

        if (role?.id) {
          navigate(role.id === 4 ? "/orders" : "/home");

          setAuthUser({
            logged: true,
            typeCustomer: typecustomer ? typecustomer : "",
            role: role.id,
            type: user_type ? user_type?.name : "",
            user: { ...data.user, confirmed: typecustomer ? typecustomer : "" },
          });

          toast.success("Successful login");
        } else {
          clearUserSession();
          toast.error("The user does not have access");
        }
      } catch (error) {
        clearUserSession();
        toast.error("Failed to fetch user details");
      }
    } catch (error) {
      clearUserSession();
      toast.error("User not found, make sure the email is correct.");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const isLoading = magicLinkLoading || mutationLoading;

  return (
    <>
      {isLoading && <Loading />}
      {magicLinkLoading && (
        <div className="message-loading">
          <div className="loading loading-card"></div>
          <p>Please wait a moment to prepare your textile library.</p>
        </div>
      )}
      <section className="container container-card-login">
        <h2>Login</h2>
        <div className="container-card-login__content">
          {loginMethod === "magiclink" && (
            <>
              <h3
                style={{
                  textAlign: "center",
                  fontWeight: "400",
                  marginBlock: "20px",
                }}
              >
                Log in with Magic Link
              </h3>
              <form onSubmit={handleMagicLinkLogin} noValidate>
                <div className="container-card-login__input">
                  <input
                    type="email"
                    name="magicEmail"
                    placeholder="E-mail address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="u-width-100"
                    style={{
                      height: "44px",
                      border: "1px solid black",
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary"
                  style={{ margin: "auto", width: "250px" }}
                >
                  {magicLinkLoading ? (
                    <div
                      className="loading"
                      style={{ width: "30px", height: "30px" }}
                    ></div>
                  ) : (
                    "Send Magic Link"
                  )}
                </button>
              </form>
            </>
          )}

          {loginMethod === "password" && (
            <>
              <h3
                style={{
                  textAlign: "center",
                  fontWeight: "400",
                  marginBlock: "20px",
                }}
              >
                LOGIN
              </h3>
              <form onSubmit={handleLogin} noValidate>
                <div className="container-card-login__input">
                  <input
                    type="text"
                    name="username"
                    placeholder="E-mail address"
                    value={credentials.username}
                    onChange={handleChange}
                    className="u-width-100"
                    style={{
                      height: "44px",
                      border: "1px solid black",
                    }}
                    required
                    disabled={isLoading}
                  />
                  {messageError.username && (
                    <p
                      style={{
                        color: "red",
                        fontSize: "14px",
                        margin: "5px 0",
                      }}
                    >
                      {messageError.username}
                    </p>
                  )}
                </div>

                <div className="container-card-login__input">
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={credentials.password}
                    onChange={handleChange}
                    className="u-width-100"
                    style={{
                      height: "44px",
                      border: "1px solid black",
                    }}
                    required
                    disabled={isLoading}
                  />
                  {messageError.password && (
                    <p
                      style={{
                        color: "red",
                        fontSize: "14px",
                        margin: "5px 0",
                      }}
                    >
                      {messageError.password}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary"
                  style={{ margin: "auto", marginBlock: "20px", width: "250px" }}
                >
                  {mutationLoading ? (
                    <div
                      className="loading"
                      style={{ width: "30px", height: "30px" }}
                    ></div>
                  ) : (
                    "Login"
                  )}
                </button>
              </form>
            </>
          )}
          <p style={{ marginBottom: "0", marginTop: "0em", color: "black" }}>
            Please agree to the{" "}
            <Link
              to="/terms-and-conditions"
              style={{ textDecoration: "underline", color: "black" }}
            >
              terms of service
            </Link>{" "}
            and proceed.
          </p>

          <p style={{ marginBottom: "10px", marginTop: "1em", color: "black" }}>
            No account?{" "}
            <Link
              to="/register-new"
              className=""
              style={{ textDecoration: "underline", color: "black" }}
            >
              Pre-register
            </Link>{" "}
            now.
          </p>
        </div>
      </section>
    </>
  );
};

export default LoginPassword;
